import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { causeQrUrl, globalQrUrl, loadRazorpayScript } from "@/lib/payments";
import { useLang } from "@/contexts/LanguageContext";
import { X, CreditCard, QrCode, Banknote, IndianRupee, HandHeart, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function DonateModal({ cause, content, open, onClose, onSuccess }) {
  const { lang } = useLang();
  const fontH = lang === "ta" ? "font-serif-ta" : "font-serif-en";
  const [amount, setAmount] = useState(500);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState("razorpay"); // razorpay | upi | bank
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/payment-settings").then((r) => setSettings(r.data)).catch(() => setSettings({ razorpay_enabled: false }));
  }, [open]);

  useEffect(() => {
    if (settings && !settings.razorpay_enabled && mode === "razorpay") setMode("upi");
  }, [settings, mode]);

  if (!open) return null;

  const title = cause ? (lang === "ta" && cause.title_ta ? cause.title_ta : cause.title_en) : "General Donation";

  const payRazorpay = async () => {
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setBusy(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load Razorpay");
      const { data } = await api.post("/donations/create-order", {
        cause_id: cause?.id || null,
        amount: parseFloat(amount),
        donor_name: name, donor_phone: phone, donor_email: email,
      });
      const rzp = new window.Razorpay({
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Masjid Al Noor Makkah Madrasa",
        description: title,
        prefill: { name, contact: phone, email },
        theme: { color: "#064E3B" },
        handler: async (res) => {
          try {
            await api.post("/donations/verify", {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            toast.success("JazakAllah Khair — donation received!");
            onSuccess?.();
            onClose();
          } catch (e2) {
            toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Verification failed");
          }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (resp) => {
        toast.error(resp?.error?.description || "Payment failed");
        setBusy(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div data-testid="donate-modal" className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl my-8 relative">
        <button data-testid="donate-close-btn" onClick={onClose} className="absolute top-3 right-3 p-2 rounded-md hover:bg-stone-100"><X className="w-5 h-5" /></button>

        <div className="p-6 border-b border-stone-200">
          <div className="flex items-center gap-2 text-amber-700 text-xs uppercase tracking-[0.25em] mb-1">
            <HandHeart className="w-4 h-4" /> Donate
          </div>
          <h2 className={`${fontH} text-2xl text-emerald-950`}>{title}</h2>
          <p className="text-sm text-stone-500 mt-1">صدقة جارية · Continuing charity</p>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Amount (₹)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[100, 500, 1000, 5000].map((v) => (
                <button key={v} data-testid={`donate-quick-${v}`} onClick={() => setAmount(v)} className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${amount == v ? "bg-emerald-900 text-white border-emerald-900" : "border-stone-300 hover:border-emerald-900"}`}>₹{v}</button>
              ))}
            </div>
            <div className="relative">
              <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <input data-testid="donate-amount-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full pl-9 pr-3 py-2.5 rounded-md border border-stone-300 focus:ring-2 focus:ring-emerald-700" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Name</label>
              <input data-testid="donate-name" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2.5 rounded-md border border-stone-300 focus:ring-2 focus:ring-emerald-700" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Phone</label>
              <input data-testid="donate-phone" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2.5 rounded-md border border-stone-300 focus:ring-2 focus:ring-emerald-700" />
            </div>
            <div className="col-span-2">
              <label className="text-xs uppercase tracking-widest text-stone-500 mb-1 block">Email (optional)</label>
              <input data-testid="donate-email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2.5 rounded-md border border-stone-300 focus:ring-2 focus:ring-emerald-700" />
            </div>
          </div>

          {/* Mode tabs */}
          <div className="flex gap-2 border-b border-stone-200 -mx-6 px-6 pt-2">
            <ModeTab id="razorpay" active={mode === "razorpay"} disabled={!settings?.razorpay_enabled} onClick={() => setMode("razorpay")} icon={CreditCard} label="Card / UPI / Netbanking" />
            <ModeTab id="upi" active={mode === "upi"} onClick={() => setMode("upi")} icon={QrCode} label="UPI / QR" />
            <ModeTab id="bank" active={mode === "bank"} onClick={() => setMode("bank")} icon={Banknote} label="Bank Transfer" />
          </div>

          {mode === "razorpay" && (
            <div>
              {settings?.razorpay_enabled ? (
                <button data-testid="donate-pay-razorpay" disabled={busy} onClick={payRazorpay} className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  <CreditCard className="w-4 h-4" /> {busy ? "Processing…" : `Pay ₹${amount} securely`}
                </button>
              ) : (
                <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                  <AlertCircle className="w-4 h-4 mt-0.5" /> Online payments are not yet configured. Please use UPI/QR or Bank Transfer below.
                </div>
              )}
            </div>
          )}

          {mode === "upi" && (
            <div className="grid sm:grid-cols-2 gap-4 items-start">
              <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 flex flex-col items-center text-center">
                {cause?.qr_path ? (
                  <img src={causeQrUrl(cause.id)} alt="QR" className="w-44 h-44 object-contain" />
                ) : settings?.has_global_qr ? (
                  <img src={globalQrUrl()} alt="QR" className="w-44 h-44 object-contain" />
                ) : (
                  <div className="w-44 h-44 flex items-center justify-center bg-white border border-dashed border-stone-300 rounded-md text-xs text-stone-400 text-center p-3">QR code not uploaded yet</div>
                )}
                <div className="text-xs uppercase tracking-widest text-stone-500 mt-3">Scan to Pay</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500">UPI ID</div>
                <div className="text-emerald-900 font-mono mt-1 break-all">{settings?.upi_id || content?.upi_id || "Not configured"}</div>
                <p className="text-xs text-stone-500 mt-3">After paying via UPI, please WhatsApp the screenshot to {content?.phone} so we can issue a receipt.</p>
              </div>
            </div>
          )}

          {mode === "bank" && (
            <div className="bg-stone-50 border border-stone-200 rounded-lg p-4 grid grid-cols-2 gap-3 text-sm">
              <Pair label="Bank" value={content?.bank_name} />
              <Pair label="Account Name" value={content?.account_name} />
              <Pair label="Account No." value={content?.account_number} mono />
              <Pair label="IFSC" value={content?.ifsc} mono />
              <div className="col-span-2 text-xs text-stone-500 flex items-start gap-2 mt-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5" /> Please share the transaction reference at <a href={`tel:${content?.phone}`} className="text-emerald-900">{content?.phone}</a> to receive a receipt.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModeTab({ id, active, disabled, onClick, icon: Icon, label }) {
  return (
    <button data-testid={`donate-mode-${id}`} disabled={disabled} onClick={onClick} className={`-mb-px flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition-colors ${disabled ? "text-stone-300 cursor-not-allowed" : active ? "border-emerald-900 text-emerald-900" : "border-transparent text-stone-500 hover:text-emerald-900"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}

function Pair({ label, value, mono }) {
  return (
    <div className="bg-white rounded-md p-2.5 border border-stone-200">
      <div className="text-[10px] uppercase tracking-widest text-stone-500">{label}</div>
      <div className={`mt-0.5 text-stone-800 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
